import type { MouseEvent as ReactMouseEvent } from "react";
import { $, _ } from "js/vendor";

export type MouseDragItem = {
    id: string;
};

type MouseDragItemsOptions<T extends MouseDragItem> = {
    event: ReactMouseEvent;
    container: HTMLElement | null;
    mousedItem: T;
    selection: T[];
    imageBorderColor?: string;
    onDidStartDrag?: () => void;
    onStartDragItems?: (items: T[]) => void;
    onDropDragItems?: (items: T[], dropEvent?: unknown) => void;
};

/**
 * Imperative mouse drag used by team library panes. Drop targets listen for
 * mouseup while draggedItems state is populated in the parent pane.
 */
export function mouseDragItems<T extends MouseDragItem>({
    event,
    container,
    mousedItem,
    selection,
    imageBorderColor = "black",
    onDidStartDrag,
    onStartDragItems,
    onDropDragItems,
}: MouseDragItemsOptions<T>) {
    const startX = event.pageX;
    const startY = event.pageY;

    let $dragSelection: ReturnType<typeof $> | undefined;
    const startDrag = () => {
        onDidStartDrag?.();
        $dragSelection = $("body").addEl($.div("dragged-items"));
        $dragSelection.css({
            position: "absolute",
            zIndex: 1000,
            width: 133,
            height: 77,
            pointerEvents: "none",
            boxShadow: "10px 10px 20px rgba(0,0,0,.4)",
        });

        for (const item of selection) {
            const $el = container ? $(container).find(`[data-item-id="${item.id}"]`) : $();
            if ($el.length) {
                const $image = $el.find(".thumbnail img").clone();
                $image.css({ position: "absolute", border: `solid 1px ${imageBorderColor}`, pointerEvents: "none" });
                $image.width(133).left(0).top(0);
                if (item.id !== mousedItem.id) {
                    $image.css({ transform: `rotate(${Math.random() * 20 - 10}deg)` });
                    $dragSelection.prepend($image);
                } else {
                    $dragSelection.append($image);
                }
            }
        }

        onStartDragItems?.(selection);
    };

    $(document).on("mousemove.drag", moveEvent => {
        if (!$dragSelection && (Math.abs(moveEvent.pageX - startX) > 5 || Math.abs(moveEvent.pageY - startY) > 5)) {
            startDrag();
        } else if ($dragSelection) {
            $dragSelection.left(moveEvent.pageX).top(moveEvent.pageY);
        }
    });

    $(document).on("mouseup.drag", dropEvent => {
        $(document).off(".drag");

        if ($dragSelection) {
            $dragSelection.remove();

            _.defer(() => {
                onDropDragItems?.(selection, dropEvent);
            });
        }
    });
}
